/**
 * useOrbitalScene — the rAF driver for the discovery scene's orbital tiles.
 *
 * Architecture (locked, do not change without an ADR):
 *   tiles never rotate. Each frame the hook computes each tile's (x, y) in
 *   stage-local pixel coords as:
 *
 *     angle  = baseAngle + ringSpin
 *     x      = cos(angle) * radius
 *     y      = sin(angle) * radius * yFlatten
 *
 *   and writes `transform: translate3d(x, y, 0)` directly to the tile DOM
 *   node. Because the tile element never has a rotateZ applied, its label
 *   is always upright at every angle. yFlatten<1 squashes Y so the
 *   circular orbit projects as an ellipse → reads as a tilted disk.
 *
 * The hook also writes a `--depth` custom property (0..1, bottom-half = 1)
 * that the CSS uses to shade tiles in the back darker than the front, plus
 * `zIndex` so the front tiles overlap the back tiles correctly.
 *
 * Reduced-motion: the hook never starts the rAF loop. Tiles still get
 * positioned exactly once at their base angle so the static layout reads.
 */
import { useCallback, useEffect, useRef } from 'react';

import { prefersReducedMotion } from '@/tokens/motion';

export type RingNumber = 1 | 2 | 3;

export interface OrbitalTileConfig {
  /** Stable id used by the parent to wire up beam/chip helpers. */
  readonly id: string;
  /** Which ring this tile rides on. */
  readonly ring: RingNumber;
  /** Base angle in degrees (0 = right, 90 = bottom of the projected ellipse). */
  readonly angleDeg: number;
}

export interface UseOrbitalSceneOptions {
  /** Tile configs in render order. */
  readonly tiles: ReadonlyArray<OrbitalTileConfig>;
  /**
   * When `true` the rAF loop drives the collapse animation:
   * radius shrinks to zero over ~800ms, simulating the tiles falling
   * into Asha's core.
   */
  readonly collapsing: boolean;
}

interface RingState {
  spin: number;
  /** Degrees per second of orbital travel. */
  readonly dpsec: number;
  /** Radius of this ring's orbit, expressed as a fraction of stage width. */
  readonly radiusFraction: number;
}

const RINGS: Readonly<Record<RingNumber, RingState>> = {
  1: { spin: 0, dpsec: 360 / 18, radiusFraction: 0.3 },
  2: { spin: 0, dpsec: -360 / 14, radiusFraction: 0.46 },
  3: { spin: 0, dpsec: 360 / 24, radiusFraction: 0.62 },
};

/** matches the stage-area's 0.6 height-to-width ratio. */
const Y_FLATTEN = 0.6;

interface RuntimeTile {
  readonly cfg: OrbitalTileConfig;
  /** baseAngle in radians, pre-converted once at mount. */
  readonly baseAngleRad: number;
  el: HTMLElement | null;
  /** 0 = on orbit, 1 = at center. Eased to 1 during the completion animation. */
  collapseProgress: number;
}

export interface UseOrbitalSceneResult {
  /** Stage container ref; must be the element whose `getBoundingClientRect` defines the orbital pixel scale. */
  readonly stageRef: React.RefCallback<HTMLDivElement>;
  /** Returns a ref callback for the tile with the given id. */
  readonly tileRefFor: (id: string) => React.RefCallback<HTMLElement>;
}

/**
 * Drives the orbital scene transforms. The hook owns:
 *   - per-ring spin state (mutable refs, never re-rendered)
 *   - per-tile collapse progress
 *   - the rAF loop lifecycle (start on mount, stop on unmount)
 *
 * Consumers attach the returned ref callbacks. They MUST NOT set
 * `style.transform` on tiles themselves.
 */
export function useOrbitalScene(
  options: UseOrbitalSceneOptions,
): UseOrbitalSceneResult {
  const { tiles, collapsing } = options;

  // We deliberately track the tiles in a ref keyed by id so the rAF loop
  // never closes over stale React state. When the tile config array
  // changes (e.g. a new connector arrives), we rebuild the runtime map.
  const runtimeRef = useRef<Map<string, RuntimeTile>>(new Map());
  const stageElRef = useRef<HTMLDivElement | null>(null);
  const ringsRef = useRef<Record<RingNumber, RingState>>({
    1: { ...RINGS[1] },
    2: { ...RINGS[2] },
    3: { ...RINGS[3] },
  });
  const collapsingRef = useRef<boolean>(collapsing);
  const rafRef = useRef<number | null>(null);
  const lastFrameRef = useRef<number>(0);

  // Keep the collapse flag in a ref so the rAF loop sees the latest value
  // without restarting.
  useEffect(() => {
    collapsingRef.current = collapsing;
    if (!collapsing) {
      // Reset collapse progress for all tiles so the scene can reopen.
      for (const t of runtimeRef.current.values()) t.collapseProgress = 0;
    }
  }, [collapsing]);

  // Rebuild the runtime tile map whenever the tile config list changes.
  // Reuse existing entries by id so we don't drop their collapse state mid-run.
  useEffect(() => {
    const next = new Map<string, RuntimeTile>();
    for (const cfg of tiles) {
      const existing = runtimeRef.current.get(cfg.id);
      next.set(cfg.id, {
        cfg,
        baseAngleRad: (cfg.angleDeg * Math.PI) / 180,
        el: existing?.el ?? null,
        collapseProgress: existing?.collapseProgress ?? 0,
      });
    }
    runtimeRef.current = next;
  }, [tiles]);

  const positionTile = useCallback(
    (t: RuntimeTile, stageWidth: number) => {
      const el = t.el;
      if (!el) return;
      const ring = ringsRef.current[t.cfg.ring];
      const spinRad = (ring.spin * Math.PI) / 180;
      const a = t.baseAngleRad + spinRad;
      const radius = ring.radiusFraction * stageWidth * (1 - t.collapseProgress);
      const x = Math.cos(a) * radius;
      const y = Math.sin(a) * radius * Y_FLATTEN;
      const depth = (Math.sin(a) + 1) / 2;
      el.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0)`;
      el.style.setProperty('--depth', depth.toFixed(3));
      el.style.zIndex = String(Math.round(depth * 100));
    },
    [],
  );

  const positionAllStatic = useCallback(() => {
    const stageEl = stageElRef.current;
    if (!stageEl) return;
    const w = stageEl.getBoundingClientRect().width;
    if (w <= 0) return;
    for (const t of runtimeRef.current.values()) {
      positionTile(t, w);
    }
  }, [positionTile]);

  // The animation loop. Lives outside React's render cycle on purpose.
  useEffect(() => {
    if (prefersReducedMotion()) {
      // Single static pass so labels don't pile up at the center on mount.
      // Defer to next frame so tile refs are attached.
      const id = window.requestAnimationFrame(positionAllStatic);
      return () => {
        window.cancelAnimationFrame(id);
      };
    }

    let cancelled = false;
    lastFrameRef.current = performance.now();

    const tick = (now: number): void => {
      if (cancelled) return;
      const stageEl = stageElRef.current;
      if (!stageEl) {
        rafRef.current = window.requestAnimationFrame(tick);
        return;
      }
      const dt = (now - lastFrameRef.current) / 1000;
      lastFrameRef.current = now;

      const w = stageEl.getBoundingClientRect().width;

      // Advance ring spin.
      const rings = ringsRef.current;
      rings[1].spin = (rings[1].spin + dt * rings[1].dpsec) % 360;
      rings[2].spin = (rings[2].spin + dt * rings[2].dpsec) % 360;
      rings[3].spin = (rings[3].spin + dt * rings[3].dpsec) % 360;

      const isCollapsing = collapsingRef.current;
      for (const t of runtimeRef.current.values()) {
        if (isCollapsing) {
          t.collapseProgress = Math.min(1, t.collapseProgress + dt / 0.8);
        }
        positionTile(t, w);
      }

      rafRef.current = window.requestAnimationFrame(tick);
    };

    rafRef.current = window.requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [positionAllStatic, positionTile]);

  const stageRef = useCallback<React.RefCallback<HTMLDivElement>>((node) => {
    stageElRef.current = node;
    if (node !== null) {
      // Run a single static pass so first paint shows tiles on their orbits
      // even before the rAF loop has had a chance to fire.
      const w = node.getBoundingClientRect().width;
      if (w > 0) {
        for (const t of runtimeRef.current.values()) {
          // Only seed if the tile hasn't been positioned yet.
          if (t.el !== null && !t.el.style.transform) {
            // mutate via positionTile to keep the math in one place
            const ring = ringsRef.current[t.cfg.ring];
            const spinRad = (ring.spin * Math.PI) / 180;
            const a = t.baseAngleRad + spinRad;
            const radius = ring.radiusFraction * w;
            const x = Math.cos(a) * radius;
            const y = Math.sin(a) * radius * Y_FLATTEN;
            t.el.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0)`;
            const depth = (Math.sin(a) + 1) / 2;
            t.el.style.setProperty('--depth', depth.toFixed(3));
            t.el.style.zIndex = String(Math.round(depth * 100));
          }
        }
      }
    }
  }, []);

  const tileRefFor = useCallback(
    (id: string): React.RefCallback<HTMLElement> =>
      (node) => {
        const entry = runtimeRef.current.get(id);
        if (!entry) return;
        entry.el = node;
        // Seed the static position immediately so the first frame is correct.
        const stage = stageElRef.current;
        if (node !== null && stage !== null) {
          const w = stage.getBoundingClientRect().width;
          if (w > 0) positionTile(entry, w);
        }
      },
    [positionTile],
  );

  return { stageRef, tileRefFor };
}
